/**
 * `<InlineError>` — renders a `user`-category error inline on a surface (frontend.md §4.2).
 *
 * frontend.md §4.2: `user` errors are "rendered inline on the surface with actionable
 * guidance ... never a toast-and-forget". A surface catches a `user`-category
 * `ErrorEnvelope` (from `useErrorHandler`, which does NOT toast user errors by default)
 * and places this where the failed content would go. An optional `onRetry` re-issues the
 * query on explicit user action (the frontend never auto-retries — conventions.md §7).
 */

import type { ErrorEnvelope } from '@/types';

interface InlineErrorProps {
  error: ErrorEnvelope;
  /** Explicit re-issue affordance — only for the genuinely retryable user error. */
  onRetry?: () => void;
}

export function InlineError({ error, onRetry }: InlineErrorProps): JSX.Element {
  return (
    <div className="inline-error" role="alert">
      <div className="inline-error__message">{error.message}</div>
      {error.queryId !== undefined && (
        <div className="inline-error__query-id">Reference: {error.queryId}</div>
      )}
      {onRetry !== undefined && (
        <button type="button" className="inline-error__retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
